import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Title from '../components/Title';
import GateLocationEditor from '../components/GateLocationEditor';
import { useSelectedSite } from '../util/DataStore';
import { useDoc } from '../util/FirebaseHooks';
import { ArgMap, FieldConfig } from '../formx/FormTypes';
import DataList from '../formx/DataList';
import { normalizeEmail } from '../formx/FormUtils';
interface UserConfig extends ArgMap {
  role: string;
  email: string;
  index?: string;
}

const listConfig = {
  name: '',
  idcol: 'index',
  dispcols: ['email', 'name', 'role'],
  editcols: ['email', 'name', 'role'],
};

const fieldConfig: FieldConfig = {
  role: {
    type: 'enum',
    title: 'Role',
    enums: [
      { key: 'user', title: 'User' },
      { key: 'guest', title: 'Guest' },
      { key: 'owner', title: 'Owner' },
      { key: 'disabled', title: 'Disabled' },
    ],
    required: true,
  },
  email: {
    type: 'email',
    title: 'Email',
    required: true,
  },
  name: {
    type: 'string',
    title: 'Name',
    required: true,
  },
};
export default function Site() {
  const location = useLocation();
  const [selectedSite, setSelectedSite] = useSelectedSite();
  const [site, updateSite] = useDoc('sites', selectedSite);
  useEffect(() => {
    const parts = location.pathname.split('/');
    const system = parts[2];
    setSelectedSite(system);
  }, [location, setSelectedSite]);

  const onRowSave = (values: ArgMap, modified: boolean) => {
    const index = values.index;
    const normalizedValues = { ...values, email: normalizeEmail(String(values.email || '')) } as UserConfig;
    const duplicateIndex = users.findIndex(
      (user, userIndex) =>
        String(userIndex) !== String(index) && normalizeEmail(user.email) === normalizedValues.email
    );
    if (duplicateIndex !== -1) {
      window.alert(`${normalizedValues.email} is already a user on this site.`);
      return;
    }
    if (index) {
      users[index] = normalizedValues;
    } else {
      users.push(normalizedValues);
    }
    const newUserList: { [email: string]: UserConfig } = {};
    users.forEach((u) => {
      delete u.index;
      const email = normalizeEmail(u.email);
      newUserList[email] = { ...u, email };
    });
    updateSite({ users: newUserList });
  };

  const onAddRow = () => {
    return { role: 'user', email: '' } as UserConfig;
  };

  const onDeleteRow = (values: ArgMap) => {
    if (!values.index) {
      return;
    }
    if (values.role === 'owner') {
      window.alert('Cannot delete user with Owner role');
      return;
    }
    const email = String(values.email || 'this user');
    if (!window.confirm(`Delete ${email} from this site?`)) {
      return;
    }
    users.splice(Number(values.index), 1);
    const newUserList: { [email: string]: UserConfig } = {};
    users.forEach((u) => {
      delete u.index;
      const email = normalizeEmail(u.email);
      newUserList[email] = { ...u, email };
    });
    updateSite({ users: newUserList });
  };

  const users = (Object.values(site?.users || {}) as UserConfig[]).map((u, i) => {
    u.index = String(i);
    return u;
  });
  return (
    <React.Fragment>
      <Title>{site?.config?.name}</Title>
      {site?.config?.systemId === selectedSite && <GateLocationEditor
        key={selectedSite}
        systemId={selectedSite}
        location={site.config.gateLocation}
      />}
      <DataList<UserConfig>
        title="Site Users"
        listConfig={listConfig}
        fieldConfig={fieldConfig}
        canEdit={true}
        canDelete={true}
        showCount={false}
        rows={users}
        onSave={onRowSave}
        onAdd={onAddRow}
        onDelete={onDeleteRow}
        orderBy="name"
      />
    </React.Fragment>
  );
}
